import React, { useEffect, useState } from "react";
import { apiClient } from "../../../lib/api";
import { useI18n } from "../../../lib/i18n";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

export default function DashBranches({ businessId }) {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name_ar: "", city: "", address_ar: "", phone: "", is_headquarters: false });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    apiClient.get(`/platform/dashboard/business/${businessId}/branches`)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [businessId]);

  const submitBranch = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post(`/platform/businesses/${businessId}/branches`, form);
      toast.success(lang === "ar" ? "تم إضافة الفرع" : "Branch added");
      setShowForm(false); setForm({ name_ar: "", city: "", address_ar: "", phone: "", is_headquarters: false });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-5" data-testid="dash-branches">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-[#8a8a8a]">{items.length} {lang === "ar" ? "فرع" : "branches"}</div>
        <button type="button" onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-bold shadow-sm"
                style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                data-testid="btn-add-branch">
          <Building2 className="w-4 h-4" /> {lang === "ar" ? "إضافة فرع" : "Add branch"}
        </button>
      </div>

      {loading ? (
        <div className="text-[#8a8a8a]">…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-white border border-dashed border-[#0a0a0a]/10 p-10 text-center text-[#8a8a8a]">
          {lang === "ar" ? "لا توجد فروع بعد" : "No branches yet"}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((b) => (
            <div key={b.id} className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-semibold text-[#0a0a0a] truncate">{b.name_ar}</div>
                  <div className="text-[12px] text-[#8a8a8a]">{b.city || "—"}</div>
                </div>
                {b.is_headquarters && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(201,165,92,0.15)", color: "#8a6f2e" }}>
                    {lang === "ar" ? "المقر الرئيسي" : "HQ"}
                  </span>
                )}
              </div>
              {b.address_ar && <div className="text-[12px] text-[#525252]">{b.address_ar}</div>}
              {b.phone && <div className="text-[12px] text-[#525252] mt-1 font-mono" dir="ltr">{b.phone}</div>}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <>
          <button type="button" aria-label="close" onClick={() => setShowForm(false)} className="fixed inset-0 z-40 bg-black/40" />
          <div className="fixed inset-x-4 top-24 sm:inset-x-auto sm:right-1/2 sm:translate-x-1/2 sm:max-w-md z-50 rounded-2xl bg-white shadow-2xl p-5">
            <form onSubmit={submitBranch} className="space-y-3">
              <h3 className="text-lg font-bold">{lang === "ar" ? "إضافة فرع" : "Add branch"}</h3>
              <input required placeholder={lang === "ar" ? "اسم الفرع" : "Branch name"} value={form.name_ar}
                     onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" />
              <input placeholder={lang === "ar" ? "المدينة" : "City"} value={form.city}
                     onChange={(e) => setForm({ ...form, city: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" />
              <input placeholder={lang === "ar" ? "العنوان" : "Address"} value={form.address_ar}
                     onChange={(e) => setForm({ ...form, address_ar: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" />
              <input placeholder={lang === "ar" ? "الهاتف" : "Phone"} value={form.phone}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" dir="ltr" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_headquarters}
                       onChange={(e) => setForm({ ...form, is_headquarters: e.target.checked })} />
                {lang === "ar" ? "هذا هو المقر الرئيسي" : "This is the headquarters"}
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-full text-sm text-[#525252]">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
                <button type="submit" disabled={submitting}
                        className="px-4 py-2 rounded-full text-sm font-bold disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}>
                  {submitting ? "…" : (lang === "ar" ? "حفظ" : "Save")}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
